import { mergeFeedCustomEventIds } from '@/helpers/feed';
import { AxiosResponse } from 'axios';

import { baseListService } from '@/services/baseService';
import { IFeedBoardApiListResponse, IFeedBoardApiRequestFilters, IFeedLiveApiListResponse, IFeedLiveApiRequestFilters, IFeedPregameApiListResponse, IFeedPregameApiRequestFilters } from '@/types';
import { IListParams } from '@/types/service';
import { AppError } from '@/errors/appError';
import { loadFeedBoard, loadFeedLive, loadFeedLiveGuest, loadFeedToday, loadFeedTodayGuest } from '@/api/feed';
import { BetSelection, EventFeedType, FeedWsResponseStatus, Jollywhere, StoreName, TradingType } from '@/constants';
import { isStoreExistByCurrentRoute, useStoreByCurrentRoute } from '@/composables/useStoreByCurrentRoute';
import { format } from 'date-fns';
import { useFeedBoardStore } from '@/stores/feedBoard';
import { useEventStore } from '@/stores/event';
import { useAppStore } from '@/stores/app';
import { useWebSocketStore } from '@/stores/webSocket';
import { globals } from '@/globals';
import { useUserStore } from '@/stores/user';
import { BettingData } from '@/models/bettingData';
import { getBetTypeIdByBetSelection } from '@/helpers';

export const feedService = {
    async loadLive(params: IListParams<IFeedLiveApiRequestFilters>): Promise<AxiosResponse<IFeedLiveApiListResponse>> {
        const feedPageStore = useStoreByCurrentRoute();
        const appStore = useAppStore();
        const userStore = useUserStore();

        feedPageStore.loading = true;
        appStore.loading = true;
        try {
            const apiCallFn = userStore.isLoggedIn() ? loadFeedLive : loadFeedLiveGuest;

            const response = await baseListService<IFeedLiveApiListResponse>(apiCallFn, params);

            feedPageStore.processLiveResponse(response.data);

            return response;
        } catch (e) {
            throw new AppError(`Unable load live feed list, request error`, { cause: e });
        } finally {
            feedPageStore.loading = false;
            appStore.loading = false;
        }
    },

    async loadToday(params: IListParams<IFeedPregameApiRequestFilters>): Promise<AxiosResponse<IFeedPregameApiListResponse>> {
        const feedPageStore = useStoreByCurrentRoute();
        const appStore = useAppStore();
        const userStore = useUserStore();

        feedPageStore.loading = true;
        appStore.loading = true;
        try {
            const apiCallFn = userStore.isLoggedIn() ? loadFeedToday : loadFeedTodayGuest;
            const response = await baseListService<IFeedPregameApiListResponse>(apiCallFn, params);

            if (params.filters.marketId === EventFeedType.Today) feedPageStore.processTodayResponse(response.data);
            if (params.filters.marketId === EventFeedType.Early) feedPageStore.processEarlyResponse(response.data);

            return response;
        } catch (e) {
            throw new AppError(`Unable load pregame list, request error`, { cause: e });
        } finally {
            feedPageStore.loading = false;
            appStore.loading = false;
        }
    },

    async loadBoard(params: IListParams<IFeedBoardApiRequestFilters>): Promise<AxiosResponse<IFeedBoardApiListResponse>> {
        const feedPageStore = useFeedBoardStore();
        const appStore = useAppStore();
        feedPageStore.loading = true;
        appStore.loading = true;
        try {
            const response = await baseListService<IFeedBoardApiListResponse>(loadFeedBoard, params);

            feedPageStore.processLiveResponse(response.data.live);
            feedPageStore.processTodayResponse(response.data.today);
            feedPageStore.processEarlyResponse(response.data.early);

            return response;
        } catch (e) {
            throw new AppError(`Unable load board list, request error`, { cause: e });
        } finally {
            feedPageStore.loading = false;
            appStore.loading = false;
        }
    },

    wsProcessLive(data: any) {
        const appStore = useAppStore();
        if (!isStoreExistByCurrentRoute()) return;
        const feedStore = useStoreByCurrentRoute();

        if (feedStore.$id !== StoreName.FeedLive && feedStore.$id !== StoreName.FeedBoard) {
            return;
        }
        if (appStore.isBrowserTabInactive && appStore.lastBrowserTabInactive && Date.now() - appStore.lastBrowserTabInactive >= 30000) {
            //30 sec
            console.log('WS live events are skipped due to inactive tab');
            return;
        }
        let status = feedStore.checkLiveResponse(data);
        if (status === FeedWsResponseStatus.Ok) {
            feedStore.processLiveResponse(data);
        } else if (status === FeedWsResponseStatus.VersionIncorrect) {
            this.wsSubscribe(true);
        }
        return;
    },

    wsProcessToday(data: any) {
        const appStore = useAppStore();
        if (!isStoreExistByCurrentRoute()) return;
        const feedStore = useStoreByCurrentRoute();

        if (feedStore.$id !== StoreName.FeedPregame && feedStore.$id !== StoreName.FeedBoard) {
            return;
        }
        if (appStore.isBrowserTabInactive && appStore.lastBrowserTabInactive && Date.now() - appStore.lastBrowserTabInactive >= 30000) {
            //30 sec
            console.log('WS today events are skipped due to inactive tab');
            return;
        }
        let status = feedStore.checkTodayResponse(data);
        if (status === FeedWsResponseStatus.Ok) {
            feedStore.processTodayResponse(data);
        } else if (status === FeedWsResponseStatus.VersionIncorrect) {
            this.wsSubscribe(true);
        }
        return;
    },

    wsProcessEarly(data: any) {
        const appStore = useAppStore();
        if (!isStoreExistByCurrentRoute()) return;
        const feedStore = useStoreByCurrentRoute();

        if (feedStore.$id !== StoreName.FeedPregame && feedStore.$id !== StoreName.FeedBoard) {
            return;
        }
        if (appStore.isBrowserTabInactive && appStore.lastBrowserTabInactive && Date.now() - appStore.lastBrowserTabInactive >= 30000) {
            //30 sec
            console.log('WS early events are skipped due to inactive tab');
            return;
        }
        let status = feedStore.checkEarlyResponse(data);
        if (status === FeedWsResponseStatus.Ok) {
            feedStore.processEarlyResponse(data);
        } else if (status === FeedWsResponseStatus.VersionIncorrect) {
            this.wsSubscribe(true);
        }
        return;
    },

    wsSubscribe(withFeedReload: boolean = false) {
        console.log('Re-subscribe for prices');
        const feedPageStore = useStoreByCurrentRoute();
        const eventStore = useEventStore();
        const appStore = useAppStore();
        const webSocketStore = useWebSocketStore();

        //const prevUserData: any = webSocketStore.userData;

        let payload: any = {
            markets: {},
            eventPopupData: null,
            bettingPopupData: {},
        };

        if (eventStore.customEventId) {
            payload.eventPopupData = {
                customEventId: eventStore.customEventId,
                tradingTypeId: TradingType.BetOrder,
                eventDate: eventStore.eventStartedAt,
            };
        }

        if (feedPageStore.$id === StoreName.FeedLive || feedPageStore.$id === StoreName.FeedBoard) {
            payload.markets[EventFeedType.Live] = {
                feedVersion: withFeedReload ? -1 : feedPageStore.liveVersion,
                filter: {
                    line: feedPageStore.filters.line,
                    periodTypeId: feedPageStore.filters.periodTypeId,
                    customEventIds: mergeFeedCustomEventIds(feedPageStore.filters.customEventId, appStore.bettingCustomEventIds).join(','),
                    marketId: feedPageStore.$id === StoreName.FeedLive ? feedPageStore.filters.eventFeedTypeId : undefined,
                    tradingTypeId: globals.router.currentRoute.value.meta.tradingTypeId,
                },
            };

            /*const prevMarket = prevUserData?.['markets']?.[EventFeedType.Live] ?? null;
            if (
                prevMarket &&
                (prevMarket['filter']['line'] !== payload.markets[EventFeedType.Live]['filter']['line'] ||
                    prevMarket['filter']['periodTypeId'] !== payload.markets[EventFeedType.Live]['filter']['periodTypeId'] ||
                    prevMarket['filter']['customEventIds'] !== payload.markets[EventFeedType.Live]['filter']['customEventIds'] ||
                    prevMarket['filter']['marketId'] !== payload.markets[EventFeedType.Live]['filter']['marketId'] ||
                    prevMarket['filter']['tradingTypeId'] !== payload.markets[EventFeedType.Live]['filter']['tradingTypeId'])
            ) {
                payload.markets[EventFeedType.Live]['feedVersion'] = -1;
            }*/

            if (payload.markets[EventFeedType.Live]['feedVersion'] === -1) {
                feedPageStore.liveVersion = 0;
            }
        }

        if ((feedPageStore.$id === StoreName.FeedPregame && feedPageStore.filters.eventFeedTypeId === EventFeedType.Today) || feedPageStore.$id === StoreName.FeedBoard) {
            payload.markets[EventFeedType.Today] = {
                feedVersion: withFeedReload ? -1 : feedPageStore.todayVersion,
                filter: {
                    line: feedPageStore.filters.line,
                    periodTypeId: feedPageStore.filters.periodTypeId,
                    customEventIds: mergeFeedCustomEventIds(feedPageStore.filters.customEventId, appStore.bettingCustomEventIds).join(','),
                    marketId: feedPageStore.$id === StoreName.FeedPregame ? feedPageStore.filters.eventFeedTypeId : undefined,
                    tradingTypeId: globals.router.currentRoute.value.meta.tradingTypeId,
                },
            };

            /*const prevMarket = prevUserData?.['markets']?.[EventFeedType.Today] ?? null;
            if (
                prevMarket &&
                (prevMarket['filter']['line'] !== payload.markets[EventFeedType.Today]['filter']['line'] ||
                    prevMarket['filter']['periodTypeId'] !== payload.markets[EventFeedType.Today]['filter']['periodTypeId'] ||
                    prevMarket['filter']['customEventIds'] !== payload.markets[EventFeedType.Today]['filter']['customEventIds'] ||
                    prevMarket['filter']['marketId'] !== payload.markets[EventFeedType.Today]['filter']['marketId'] ||
                    prevMarket['filter']['tradingTypeId'] !== payload.markets[EventFeedType.Today]['filter']['tradingTypeId'])
            ) {
                payload.markets[EventFeedType.Today]['feedVersion'] = -1;
            }*/

            if (payload.markets[EventFeedType.Today]['feedVersion'] === -1) {
                feedPageStore.todayVersion = 0;
            }
        }

        if ((feedPageStore.$id === StoreName.FeedPregame && feedPageStore.filters.eventFeedTypeId === EventFeedType.Early) || feedPageStore.$id === StoreName.FeedBoard) {
            payload.markets[EventFeedType.Early] = {
                feedVersion: withFeedReload ? -1 : feedPageStore.earlyVersion,
                filter: {
                    line: feedPageStore.filters.line,
                    periodTypeId: feedPageStore.filters.periodTypeId,
                    customEventIds: mergeFeedCustomEventIds(feedPageStore.filters.customEventId, appStore.bettingCustomEventIds).join(','),
                    marketId: feedPageStore.$id === StoreName.FeedPregame ? feedPageStore.filters.eventFeedTypeId : undefined,
                    tradingTypeId: globals.router.currentRoute.value.meta.tradingTypeId,
                    date: feedPageStore.$id === StoreName.FeedPregame ? (feedPageStore.filters.search ? null : format(feedPageStore.filters.date, 'yyyy-MM-dd')) : undefined,
                    search: feedPageStore.$id === StoreName.FeedPregame && feedPageStore.filters.search ? feedPageStore.filters.search : undefined,
                },
            };

            /*const prevMarket = prevUserData?.['markets']?.[EventFeedType.Early] ?? null;
            if (
                prevMarket &&
                (prevMarket['filter']['line'] !== payload.markets[EventFeedType.Early]['filter']['line'] ||
                    prevMarket['filter']['periodTypeId'] !== payload.markets[EventFeedType.Early]['filter']['periodTypeId'] ||
                    prevMarket['filter']['customEventIds'] !== payload.markets[EventFeedType.Early]['filter']['customEventIds'] ||
                    prevMarket['filter']['marketId'] !== payload.markets[EventFeedType.Early]['filter']['marketId'] ||
                    prevMarket['filter']['tradingTypeId'] !== payload.markets[EventFeedType.Early]['filter']['tradingTypeId'] ||
                    prevMarket['filter']['date'] !== payload.markets[EventFeedType.Early]['filter']['date'] ||
                    prevMarket['filter']['search'] !== payload.markets[EventFeedType.Early]['filter']['search'])
            ) {
                payload.markets[EventFeedType.Early]['feedVersion'] = -1;
            }*/

            if (payload.markets[EventFeedType.Early]['feedVersion'] === -1) {
                feedPageStore.earlyVersion = 0;
            }
        }

        payload.pageCode = globals.router.currentRoute.value.name === 'favourite' ? 'board' : globals.router.currentRoute.value.name; // todo change it
        payload.pageUrl = window.location.toString();
        appStore.feedSubscribeId++;
        payload.subscribeId = appStore.feedSubscribeId;
        payload = this.bettingPopupDataToPayload(payload);

        webSocketStore.sendObj({
            event: 'updateUserData',
            data: payload,
        });

        webSocketStore.setUserData(payload);
    },

    bettingPopupDataToPayload(payload: any) {
        payload['bettingPopupData'] = {};
        const userStore = useUserStore();
        const appStore = useAppStore();

        let betFeedDataTotal: Record<string, BettingData> = {};

        betFeedDataTotal = toRaw(appStore.betFeedData);

        Object.keys(betFeedDataTotal).forEach((key: string) => {
            let handicap: string | null = null;
            if (betFeedDataTotal[key].handicap !== null) {
                handicap = betFeedDataTotal[key].handicap;

                if (
                    (Number(handicap) !== 0 && betFeedDataTotal[key].betSelectionId === BetSelection.HandicapHome && betFeedDataTotal[key].jollywhere === Jollywhere.Home) ||
                    (Number(handicap) !== 0 && betFeedDataTotal[key].betSelectionId === BetSelection.HandicapAway && betFeedDataTotal[key].jollywhere === Jollywhere.Away) ||
                    (Number(handicap) !== 0 && betFeedDataTotal[key].betSelectionId === BetSelection.CornersHandicapHome && betFeedDataTotal[key].jollywhere === Jollywhere.Home) ||
                    (Number(handicap) !== 0 && betFeedDataTotal[key].betSelectionId === BetSelection.CornersHandicapAway && betFeedDataTotal[key].jollywhere === Jollywhere.Away)
                ) {
                    handicap = '-' + handicap;
                }
            }

            payload['bettingPopupData'][key] = {
                uuid: betFeedDataTotal[key].uuid,
                site_ids: userStore.settings.bet_order_site_ids_allowed,
                bet_selection_id: betFeedDataTotal[key].betSelectionId,
                bet_type_id: getBetTypeIdByBetSelection(betFeedDataTotal[key].betSelectionId),
                market_id: betFeedDataTotal[key].eventFeedType,
                period_type_id: betFeedDataTotal[key].periodTypeId,
                custom_event_id: betFeedDataTotal[key].customEventId,
                jollywhere: betFeedDataTotal[key].jollywhere === undefined ? null : betFeedDataTotal[key].jollywhere,
                is_live: betFeedDataTotal[key].eventFeedType === EventFeedType.Live,
                //todo need use corners for corner markets
                score: betFeedDataTotal[key].eventFeedType === EventFeedType.Live ? betFeedDataTotal[key].scoreFull : '0-0',
                handicap: handicap,
                correct_score: betFeedDataTotal[key].correctScore ?? null,
            };
        });

        return payload;
    },
};
